import {mezclarPendientes, type DecisionCola} from '@/lib/bateria-cola'

/** Decisiones que no alcanzaron a llegar al servidor, guardadas en el aparato por evaluación. El
 * POST es idempotente por `ref`, así que reenviarlas no duplica nada. */
const PREFIJO = 'kairos.bateria.pendientes.'

/** Pasada una semana la decisión guardada deja de ser confiable: el lote pudo re-escanearse u otra
 * persona pudo resolverla. */
export const TTL_PENDIENTES_MS = 7 * 24 * 60 * 60 * 1000

export type AlmacenLocal = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

export function clavePendientes(businessId: string, evaluacionId: string): string {
  return `${PREFIJO}${businessId}.${evaluacionId}`
}

type Guardado = {guardadoEn: number; decisiones: DecisionCola[]}

/** Mandar al servidor una fila incompleta sería peor que perderla. */
function esDecision(valor: unknown): valor is DecisionCola {
  if (typeof valor !== 'object' || valor === null) return false
  const fila = valor as Record<string, unknown>
  return typeof fila.tipo === 'string' && typeof fila.ref === 'string'
}

function leerCrudo(almacen: AlmacenLocal, clave: string): Guardado | null {
  let texto: string | null = null
  try {
    texto = almacen.getItem(clave)
  } catch {
    return null
  }
  if (!texto) return null
  try {
    const dato = JSON.parse(texto) as unknown
    if (typeof dato !== 'object' || dato === null) return null
    const fila = dato as Record<string, unknown>
    if (typeof fila.guardadoEn !== 'number' || !Array.isArray(fila.decisiones)) return null
    return {guardadoEn: fila.guardadoEn, decisiones: fila.decisiones.filter(esDecision)}
  } catch {
    return null
  }
}

function escribir(almacen: AlmacenLocal, clave: string, decisiones: DecisionCola[], ahora: number): void {
  try {
    if (decisiones.length === 0) almacen.removeItem(clave)
    else almacen.setItem(clave, JSON.stringify({guardadoEn: ahora, decisiones} satisfies Guardado))
  } catch {
    // Sin cupo o sin permiso: la decisión sigue en pantalla y en la cola de reintento en memoria.
  }
}

/** Mezcla por `(tipo, ref)`: la última corrección gana y dos pestañas suman, no se pisan. */
export function guardarPendientes(
  almacen: AlmacenLocal,
  clave: string,
  nuevas: DecisionCola[],
  ahora: number = Date.now()
): void {
  const previas = leerCrudo(almacen, clave)?.decisiones ?? []
  escribir(almacen, clave, mezclarPendientes(previas, nuevas), ahora)
}

export function leerPendientes(almacen: AlmacenLocal, clave: string, ahora: number = Date.now()): DecisionCola[] {
  const guardado = leerCrudo(almacen, clave)
  if (!guardado) return []
  if (ahora - guardado.guardadoEn > TTL_PENDIENTES_MS) {
    borrarPendientes(almacen, clave)
    return []
  }
  return guardado.decisiones
}

/** Solo lo enviado: borrar todo botaría lo que se tecleó mientras el POST viajaba. */
export function olvidarPendientes(
  almacen: AlmacenLocal,
  clave: string,
  enviadas: DecisionCola[],
  ahora: number = Date.now()
): void {
  const guardado = leerCrudo(almacen, clave)
  if (!guardado) return
  const idas = new Set(enviadas.map((decision) => `${decision.tipo}:${decision.ref}`))
  escribir(
    almacen,
    clave,
    guardado.decisiones.filter((decision) => !idas.has(`${decision.tipo}:${decision.ref}`)),
    ahora
  )
}

export function borrarPendientes(almacen: AlmacenLocal, clave: string): void {
  try {
    almacen.removeItem(clave)
  } catch {
    // Un almacén inaccesible no puede tumbar la revisión.
  }
}
